import React, { useState } from "react";
// @ts-ignore
import PaystackPop from "@paystack/inline-js";
import useRequest from "../../hook/use-req";

// Type definitions for Paystack
interface PaystackTransaction {
  reference: string;
  trans: string;
  status: string;
  message: string;
  transaction: string;
  trxref: string;
}

interface AddFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  onPaymentSuccess?: (transaction: PaystackTransaction) => void;
}

const AddFundsModal: React.FC<AddFundsModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  onPaymentSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");

  // Paystack's own onSuccess only means the widget completed on the client —
  // it is not proof the money actually landed. The balance only moves once
  // sms-portal-backend has independently verified the transaction with
  // Paystack's API, via wallet.py's POST /wallet/payments/confirm.
  const { makeRequest: confirmPayment } = useRequest("/wallet/payments/confirm", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const handlePayment = () => {
    const parsedAmount = parseFloat(amount);

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    setLoading(true);

    const popup = new PaystackPop();

    popup.newTransaction({
      key: "pk_test_22d00c9ab7d626f773d99f5012a4b6dded972340",
      amount: parsedAmount * 100, // Convert to kobo (for NGN) or smallest currency unit
      email: userEmail,
      currency: "NGN",
      reference: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      onSuccess: async (transaction: PaystackTransaction) => {
        const [response, status] = await confirmPayment({
          reference: transaction.reference,
          payment_gateway: "paystack",
        });

        setLoading(false);

        if (status >= 200 && status < 300 && response?.status !== false) {
          alert(`Payment successful! Reference: ${transaction.reference}`);

          if (onPaymentSuccess) {
            onPaymentSuccess(transaction);
          }

          setAmount("");
          onClose();
        } else {
          // Paystack reported success but the backend couldn't verify it —
          // don't tell the user funds landed, and keep the modal open with
          // the reference visible so they can retry or hand it to support.
          alert(
            response?.message ||
              `We couldn't verify this payment yet. If you were charged, contact support with reference ${transaction.reference}.`
          );
        }
      },
      onCancel: () => {
     
        setLoading(false);
        // Don't close the modal on cancel, let user try again or manually close
      },
      onError: (error: any) => {
       
        alert("Payment failed. Please try again.");
        setLoading(false);
      },
      onLoad: () => {
  
        // Optional: You can add loading indicators here
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Add Funds</h2>
          <button
            onClick={onClose}
            className="text-[#7B7B7B] font-semibold hover:text-gray-600 text-xl font-bold"
            disabled={loading}
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Amount
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            disabled={loading}
            min="1"
            step="0.01"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={loading || !amount || parseFloat(amount) <= 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Processing..." : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFundsModal;